import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  ListItem,
  ListItemText,
  Grid,
  Card,
  ListItemIcon,
  Box,
  Stack,
  ListItemButton,
  CircularProgress,
  Button,
  Typography,
} from '@mui/material'
import { orange } from '@mui/material/colors'
import { shallowEqual, useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { selectAllRoutesMetaData, selectRoutesByDayLoading } from 'slices/configuredService/configuredServiceScheduleSlice'
import { sumBy } from 'lodash'
import T from 'T'

const RoutesLegends = ({ routes: routesProp, isLoading: isLoadingProp }) => {
  const { setValue, watch } = useFormContext()
  const reduxRoutes = useSelector(selectAllRoutesMetaData, shallowEqual)
  const reduxIsLoading = useSelector(selectRoutesByDayLoading)
  const routes = routesProp ?? reduxRoutes
  const isLoading = isLoadingProp ?? reduxIsLoading

  const totalStops = useMemo(() => sumBy(routes, 'stopCount'), [routes])

  // Initialize selectedRoutes form fields to true when route list loads.
  // Uses route.id as the key, matching the shape read by useProximityMapFormState.
  useEffect(() => {
    if (!routes?.length) return
    routes.forEach(route => {
      if (watch(`selectedRoutes.${route.id}`) === undefined) {
        setValue(`selectedRoutes.${route.id}`, true)
      }
    })
  }, [routes, setValue, watch])

  const handleToggleRoute = useCallback(
    routeId => {
      const allSelected = routes.every(r => watch(`selectedRoutes.${r.id}`) !== false)
      if (allSelected) {
        // Isolate: deselect everything except the clicked route
        routes.forEach(r => setValue(`selectedRoutes.${r.id}`, r.id === routeId))
      } else {
        setValue(`selectedRoutes.${routeId}`, !watch(`selectedRoutes.${routeId}`))
      }
    },
    [routes, setValue, watch]
  )

  const handleSelectAll = useCallback(() => {
    routes.forEach(route => setValue(`selectedRoutes.${route.id}`, true))
  }, [routes, setValue])

  return (
    <Card
      sx={{
        width: '100%',
        height: '260px',
        overflowY: 'auto',
      }}
      variant="outlined"
    >
      <Grid container>
        <Grid item size={12}>
          <ListItem dense divider>
            <ListItemText
              primary={`${totalStops} stops found`}
              secondary="within selected radius"
              slotProps={{
                primary: {
                  color: 'textPrimary',
                },
              }}
            />
            <Button size="small" onClick={handleSelectAll} sx={{ minWidth: 'unset', px: 0.5 }}>
              <Typography variant="caption">{T.SELECT_ALL}</Typography>
            </Button>
          </ListItem>
        </Grid>

        {isLoading && (
          <Grid item size={12}>
            <ListItem dense>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CircularProgress size={16} />
              </ListItemIcon>
              <ListItemText
                primary="Fetching data..."
                slotProps={{
                  primary: { variant: 'caption', color: 'textSecondary' },
                }}
              />
            </ListItem>
          </Grid>
        )}

        {routes.map(route => {
          const isSelected = watch(`selectedRoutes.${route.id}`) !== false
          return (
            <Grid item size={6} key={route.id}>
              <ListItemButton dense onClick={() => handleToggleRoute(route.id)} sx={{ opacity: isSelected ? 1 : 0.35 }}>
                <ListItemIcon
                  sx={{
                    minWidth: 'unset',
                    pr: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="center">
                    <Box width={15} height={15} bgcolor={route.color || orange[300]} />
                  </Stack>
                </ListItemIcon>
                <ListItemText
                  primary={route.name}
                  secondary={(() => {
                    const stopsCount = route.stopCount
                    return `${stopsCount} ${stopsCount === 1 ? 'stop' : 'stops'}`
                  })()}
                  slotProps={{
                    primary: {
                      variant: 'caption',
                      color: 'textPrimary',
                    },
                    secondary: {
                      variant: 'caption',
                      color: 'textSecondary',
                    },
                  }}
                />
              </ListItemButton>
            </Grid>
          )
        })}
      </Grid>
    </Card>
  )
}

RoutesLegends.propTypes = {
  routes: PropTypes.array,
  isLoading: PropTypes.bool,
}

export default RoutesLegends
